import React, { useState, useEffect } from "react";
import {
  getCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getCampaignStats,
} from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Megaphone,
  Users,
  BarChart3,
  Power,
  Target,
  TrendingUp,
  Package,
  UserCheck,
  Loader2,
  Calendar,
} from "lucide-react";

export const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedStats, setSelectedStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#8B5CF6",
    isActive: true,
    isData: false,
  });

  const fetchCampaigns = async () => {
    try {
      const response = await getCampaigns();
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des campagnes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleOpenForm = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name,
        description: campaign.description || "",
        color: campaign.color,
        isActive: campaign.isActive,
        isData: campaign.isData || false,
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        name: "",
        description: "",
        color: "#8B5CF6",
        isActive: true,
        isData: false,
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingCampaign(null);
    setFormData({
      name: "",
      description: "",
      color: "#8B5CF6",
      isActive: true,
      isData: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Le nom de la campagne est requis");
      return;
    }

    try {
      if (editingCampaign) {
        await updateCampaign(editingCampaign.id, formData);
        toast.success("Campagne mise à jour avec succès");
      } else {
        await createCampaign(formData);
        toast.success("Campagne créée avec succès");
      }
      handleCloseForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (force = false) => {
    if (!campaignToDelete) return;

    try {
      await deleteCampaign(campaignToDelete.id, force);
      toast.success("Campagne supprimée avec succès");
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleViewStats = async (campaign) => {
    setLoadingStats(true);
    setStatsDialogOpen(true);
    try {
      const response = await getCampaignStats(campaign.id);
      setSelectedStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
      setStatsDialogOpen(false);
    } finally {
      setLoadingStats(false);
    }
  };

  const colorPresets = [
    "#3B82F6", // Blue
    "#8B5CF6", // Purple
    "#10B981", // Green
    "#F59E0B", // Yellow
    "#EF4444", // Red
    "#EC4899", // Pink
    "#06B6D4", // Cyan
    "#1877F2", // Facebook Blue
    "#EA4335", // Google Red
    "#6B7280", // Gray
  ];

  // Calculate totals
  const totalLeads = campaigns.reduce((sum, c) => sum + (c.leadCount || 0), 0);
  const activeCampaigns = campaigns.filter((c) => c.isActive).length;
  const avgLeadsPerCampaign = campaigns.length > 0 ? Math.round(totalLeads / campaigns.length) : 0;
  const topCampaign = campaigns.length > 0
    ? campaigns.reduce((max, c) => (c.leadCount || 0) > (max.leadCount || 0) ? c : max, campaigns[0])
    : null;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="campaigns-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Campagnes
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos campagnes marketing et suivez leurs performances
          </p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-campaign-button">
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle campagne
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total campagnes</p>
                <p className="text-3xl font-bold">{campaigns.length}</p>
              </div>
              <Megaphone className="h-10 w-10 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Campagnes actives</p>
                <p className="text-3xl font-bold text-green-600">{activeCampaigns}</p>
              </div>
              <Power className="h-10 w-10 text-green-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total leads</p>
                <p className="text-3xl font-bold">{totalLeads}</p>
              </div>
              <Users className="h-10 w-10 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top campagne</p>
                <p className="text-xl font-bold truncate max-w-[120px]" title={topCampaign?.name}>
                  {topCampaign?.name || "-"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {topCampaign?.leadCount || 0} leads
                </p>
              </div>
              <Target className="h-10 w-10 text-orange-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des campagnes</CardTitle>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucune campagne créée</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => handleOpenForm()}
              >
                Créer la première campagne
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map((campaign) => (
                <Card
                  key={campaign.id}
                  className={`relative overflow-hidden hover:shadow-md transition-shadow ${
                    !campaign.isActive ? "opacity-60" : ""
                  }`}
                  data-testid={`campaign-card-${campaign.id}`}
                >
                  <div
                    className="absolute top-0 left-0 w-full h-1"
                    style={{ backgroundColor: campaign.color }}
                  />
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${campaign.color}20` }}
                        >
                          <Megaphone
                            className="h-5 w-5"
                            style={{ color: campaign.color }}
                          />
                        </div>
                        <div>
                          <CardTitle className="text-base">
                            {campaign.name}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              variant={campaign.isActive ? "default" : "secondary"}
                              className={
                                campaign.isActive
                                  ? "bg-green-100 text-green-700 hover:bg-green-100"
                                  : ""
                              }
                            >
                              {campaign.isActive ? "Active" : "Inactive"}
                            </Badge>
                            {campaign.isData && (
                              <Badge variant="outline" className="text-amber-700 border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-xs">
                                Data
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleViewStats(campaign)}
                          >
                            <BarChart3 className="h-4 w-4 mr-2" />
                            Statistiques
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleOpenForm(campaign)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setCampaignToDelete(campaign);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {campaign.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {campaign.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{campaign.leadCount || 0}</span>
                        <span className="text-muted-foreground">leads</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewStats(campaign)}
                        className="text-xs"
                      >
                        <BarChart3 className="h-3 w-3 mr-1" />
                        Voir stats
                      </Button>
                    </div>
                    {/* Mini progress bar */}
                    {totalLeads > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Part du total</span>
                          <span>{Math.round(((campaign.leadCount || 0) / totalLeads) * 100)}%</span>
                        </div>
                        <Progress
                          value={((campaign.leadCount || 0) / totalLeads) * 100}
                          className="h-1.5"
                        />
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-3">
                      Créée le {formatDate(campaign.createdAt)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaign Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? "Modifier la campagne" : "Nouvelle campagne"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la campagne</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Ex: Black Friday 2024, Salon Pro..."
                data-testid="campaign-form-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optionnel)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Description de la campagne..."
                data-testid="campaign-form-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex flex-wrap gap-2">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, color }))}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                      formData.color === color ? "ring-2 ring-offset-2 ring-primary" : ""
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                  placeholder="#8B5CF6"
                  className="flex-1 font-mono"
                  data-testid="campaign-form-color"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="isActive">Campagne active</Label>
                <p className="text-xs text-muted-foreground">
                  Une campagne inactive ne sera pas proposée lors des imports
                </p>
              </div>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(v) => setFormData((prev) => ({ ...prev, isActive: v }))}
                data-testid="campaign-form-active"
              />
            </div>

            <div className="flex items-center justify-between py-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3">
              <div>
                <Label htmlFor="isData" className="flex items-center gap-2">
                  <span>Campagne de type "Data"</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Les leads Data ne sont pas comptés dans le taux de transformation global — ils ont leurs propres stats
                </p>
              </div>
              <Switch
                id="isData"
                checked={formData.isData}
                onCheckedChange={(v) => setFormData((prev) => ({ ...prev, isData: v }))}
                data-testid="campaign-form-isdata"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button type="submit" data-testid="campaign-form-submit">
                {editingCampaign ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette campagne ?</AlertDialogTitle>
            <AlertDialogDescription>
              {campaignToDelete?.leadCount > 0
                ? `Cette campagne a ${campaignToDelete.leadCount} leads associés. Voulez-vous vraiment la supprimer ? Les leads ne seront pas supprimés mais perdront leur association à cette campagne.`
                : "Cette action est irréversible. La campagne sera définitivement supprimée."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(campaignToDelete?.leadCount > 0)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stats Dialog */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Statistiques de la campagne
            </DialogTitle>
          </DialogHeader>

          {loadingStats ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : selectedStats && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${selectedStats.campaign.color}20` }}
                >
                  <Megaphone
                    className="h-6 w-6"
                    style={{ color: selectedStats.campaign.color }}
                  />
                </div>
                <div>
                  <h3 className="font-semibold">{selectedStats.campaign.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedStats.totalLeads} leads au total
                  </p>
                </div>
                <Badge
                  className="ml-auto"
                  variant={selectedStats.campaign.isActive ? "default" : "secondary"}
                >
                  {selectedStats.campaign.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{selectedStats.totalLeads}</p>
                    <p className="text-xs text-muted-foreground">Total leads</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Target className="h-6 w-6 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">{selectedStats.convertedLeads}</p>
                    <p className="text-xs text-muted-foreground">Convertis</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                    <p className="text-2xl font-bold">{selectedStats.conversionRate}%</p>
                    <p className="text-xs text-muted-foreground">Taux conversion</p>
                  </CardContent>
                </Card>
              </div>

              {/* Leads by Status */}
              {selectedStats.leadsByStatus?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Répartition par statut
                  </h4>
                  <div className="space-y-2">
                    {selectedStats.leadsByStatus.map((status) => (
                      <div
                        key={status.statusId}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: status.statusColor }}
                          />
                          <span className="text-sm">{status.statusName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{status.count}</Badge>
                          <span className="text-xs text-muted-foreground">
                            ({Math.round((status.count / selectedStats.totalLeads) * 100)}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads by Source */}
              {selectedStats.leadsBySource?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Leads par source
                  </h4>
                  <div className="space-y-2">
                    {selectedStats.leadsBySource.slice(0, 5).map((source) => (
                      <div
                        key={source.sourceId}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <span className="text-sm">{source.sourceName}</span>
                        <Badge variant="secondary">{source.count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads by Seller */}
              {selectedStats.leadsBySeller?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <UserCheck className="h-4 w-4" />
                    Leads par vendeur
                  </h4>
                  <div className="space-y-2">
                    {selectedStats.leadsBySeller.slice(0, 5).map((seller) => (
                      <div
                        key={seller.sellerId}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <span className="text-sm">{seller.sellerName}</span>
                        <Badge variant="secondary">{seller.count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads Over Time */}
              {selectedStats.leadsOverTime?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Leads sur les 30 derniers jours
                  </h4>
                  <div className="p-3 rounded-lg border">
                    <div className="flex items-end gap-1 h-20">
                      {selectedStats.leadsOverTime.map((day, index) => {
                        const maxCount = Math.max(...selectedStats.leadsOverTime.map(d => d.count));
                        const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                        return (
                          <div
                            key={index}
                            className="flex-1 rounded-t transition-all hover:opacity-80"
                            style={{ 
                              height: `${Math.max(height, 5)}%`,
                              backgroundColor: selectedStats.campaign.color
                            }}
                            title={`${day.date}: ${day.count} leads`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                      <span>{selectedStats.leadsOverTime[0]?.date}</span>
                      <span>{selectedStats.leadsOverTime[selectedStats.leadsOverTime.length - 1]?.date}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {selectedStats.totalLeads === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun lead dans cette campagne</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
